import { createElement, isValidElement, type ElementType, type ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "../../lib/utils";

type StatCardColor = "primary" | "success" | "warning" | "danger";

const colorClasses: Record<StatCardColor, string> = {
  primary: "bg-primary-50 text-primary-600",
  success: "bg-green-50 text-green-600",
  warning: "bg-yellow-50 text-yellow-600",
  danger: "bg-red-50 text-red-600",
};

function isIconComponent(icon: ReactNode | ElementType): icon is ElementType {
  if (isValidElement(icon)) return false;
  return typeof icon === "function" || (typeof icon === "object" && icon !== null && "render" in icon);
}

function renderIcon(icon: ReactNode | ElementType, className: string) {
  if (isIconComponent(icon)) {
    return createElement(icon, { className });
  }
  return icon;
}

export function StatCard({
  label,
  title,
  value,
  icon,
  sub,
  capitalizeValue = false,
  trend,
  trendValue,
  color = "primary",
  positive,
  highlight,
}: {
  label?: string;
  title?: string;
  value: string | number;
  icon: ReactNode | ElementType;
  sub?: string;
  capitalizeValue?: boolean;
  trend?: "up" | "down";
  trendValue?: string;
  color?: StatCardColor;
  positive?: boolean;
  highlight?: boolean;
}) {
  const heading = label ?? title ?? "";
  const isElementType = isIconComponent(icon);

  if (title !== undefined) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
            {trend && trendValue && (
              <div className="flex items-center gap-1 mt-2">
                {trend === "up" ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )}
                <span
                  className={`text-sm ${
                    trend === "up" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {trendValue}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            {renderIcon(icon, "w-6 h-6")}
          </div>
        </div>
      </div>
    );
  }

  if (positive !== undefined || highlight || isElementType) {
    return (
      <div
        className={cn(
          "bg-white dark:bg-gray-800 rounded-xl p-5 shadow-xs border",
          highlight
            ? "border-indigo-400 dark:border-indigo-500"
            : "border-gray-200 dark:border-gray-700",
        )}
      >
        <div className="flex items-center gap-3 mb-2">
          <div
            className={cn(
              "p-2 rounded-lg",
              positive === undefined
                ? "bg-gray-100 dark:bg-gray-700"
                : positive
                  ? "bg-green-100 dark:bg-green-900/40"
                  : "bg-red-100 dark:bg-red-900/40",
            )}
          >
            {renderIcon(
              icon,
              cn(
                "w-4 h-4",
                positive === undefined
                  ? "text-gray-500"
                  : positive
                    ? "text-green-600"
                    : "text-red-600",
              ),
            )}
          </div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {heading}
          </span>
        </div>
        <p
          className={cn(
            "text-xl font-bold",
            positive === undefined
              ? "text-gray-900 dark:text-white"
              : positive
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400",
          )}
        >
          {value}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 card">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg dark:bg-gray-700">{icon as ReactNode}</div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{heading}</p>
          <p
            className={cn(
              "text-xl font-bold text-gray-900 dark:text-white",
              capitalizeValue && "capitalize",
            )}
          >
            {value}
          </p>
          {sub && <p className="text-xs text-gray-400">{sub}</p>}
        </div>
      </div>
    </div>
  );
}
